import { useState, useEffect, useMemo, useCallback } from 'react';
import { api } from '@/utils/api';
import {
  Phone, PhoneIncoming, Clock, Target, Users, TrendingUp,
  Loader2, ChevronDown, ChevronRight, X, Award, BarChart3,
  PhoneCall, CalendarCheck, Timer, UserCheck, Shield,
} from 'lucide-react';

// ─── Types ───

type Period = 'today' | 'week' | 'month';
type UserRole = 'executive' | 'manager' | 'admin';

interface AgentInfo {
  id: string;
  firstName: string;
  lastName: string;
}

interface MemberStats {
  agent: AgentInfo;
  role: string;
  totalCalls: number;
  connectedCalls: number;
  totalDuration: number;
  avgDuration: number;
  conversions: number;
  followUpsSet: number;
  followUpsDone: number;
  connectRate: number;
}

interface AggregateStats {
  totalCalls: number;
  connectedCalls: number;
  totalDuration: number;
  avgDuration: number;
  conversions: number;
  followUpsSet: number;
  followUpsDone: number;
  connectRate: number;
}

interface TeamData {
  teamName: string;
  memberCount: number;
  dealsWon: number;
  pendingFollowUps: number;
  aggregate: AggregateStats;
  members: MemberStats[];
}

interface TeamInfo {
  id: string;
  name: string;
  myRole: string;
}

interface MemberDetail {
  agent: AgentInfo;
  role: string;
  totalCalls: number;
  connectedCalls: number;
  totalDuration: number;
  avgDuration: number;
  conversions: number;
  followUpsSet: number;
  followUpsDone: number;
  connectRate: number;
  dealsWon: number;
  dealValueWon: number;
  pendingFollowUps: number;
  overdueFollowUps: number;
  leadsAssigned: number;
  callsByStatus?: Record<string, number>;
  recentCalls?: Array<{
    id: string;
    phoneNumber: string;
    status: string;
    duration: number;
    startTime: string;
    notes?: string;
    callSummary?: string;
    contactName?: string;
  }>;
  recentInteractions?: Array<{
    id: string;
    type: string;
    summary?: string;
    outcome?: string;
    contactName: string;
    createdAt: string;
  }>;
}

interface LeaderboardEntry {
  rank: number;
  agent: AgentInfo;
  totalCalls: number;
  connectedCalls: number;
  conversions: number;
  connectRate: number;
}

// ─── Helpers ───

const PERIOD_LABELS: Record<Period, string> = {
  today: 'Today',
  week: 'This Week',
  month: 'This Month',
};

function formatDuration(seconds: number): string {
  if (!seconds || seconds <= 0) return '0:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function formatDurationShort(seconds: number): string {
  if (!seconds || seconds <= 0) return '0m';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function formatRate(rate: number): string {
  // Backend sends connectRate as 0-100 integer
  return `${rate.toFixed(0)}%`;
}

function getRateColor(rate: number): string {
  // rate is 0-100
  if (rate >= 60) return 'text-green-600 dark:text-green-400';
  if (rate >= 35) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
}

function getRateBg(rate: number): string {
  if (rate >= 60) return 'bg-green-50 dark:bg-green-900/20';
  if (rate >= 35) return 'bg-amber-50 dark:bg-amber-900/20';
  return 'bg-red-50 dark:bg-red-900/20';
}

// ─── KPI Card ───

function KpiCard({
  icon: Icon,
  label,
  value,
  subValue,
  color = 'text-gray-600 dark:text-gray-300',
  iconBg = 'bg-gray-100 dark:bg-gray-700',
}: {
  icon: typeof Phone;
  label: string;
  value: string | number;
  subValue?: string;
  color?: string;
  iconBg?: string;
}) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 flex items-start gap-3">
      <div className={`p-2.5 rounded-lg ${iconBg} shrink-0`}>
        <Icon className={`w-5 h-5 ${color}`} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide">{label}</p>
        <p className="text-xl font-bold text-gray-900 dark:text-white mt-0.5">{value}</p>
        {subValue && <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{subValue}</p>}
      </div>
    </div>
  );
}

// ─── Member Detail Modal ───

function MemberDetailModal({
  member,
  detail,
  loading,
  period,
  onClose,
}: {
  member: MemberStats;
  detail: MemberDetail | null;
  loading: boolean;
  period: Period;
  onClose: () => void;
}) {
  const stats = detail || member;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {member.agent.firstName} {member.agent.lastName}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 capitalize">{member.role.toLowerCase().replace(/_/g, ' ')} &middot; {PERIOD_LABELS[period]}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
          </div>
        ) : (
          <div className="px-6 py-5 space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatBox label="Total Calls" value={stats.totalCalls} />
              <StatBox label="Connected" value={stats.connectedCalls} />
              <StatBox label="Connect Rate" value={formatRate(stats.connectRate)} highlight={getRateColor(stats.connectRate)} />
              <StatBox label="Deals Won" value={detail?.dealsWon ?? stats.conversions} />
              <StatBox label="Talk Time" value={formatDuration(stats.totalDuration)} />
              <StatBox label="Avg Duration" value={formatDuration(stats.avgDuration)} />
              <StatBox label="Follow-ups Set" value={stats.followUpsSet} />
              <StatBox label="Follow-ups Done" value={stats.followUpsDone} />
              {detail && (
                <>
                  <StatBox label="Overdue F/U" value={detail.overdueFollowUps} highlight={detail.overdueFollowUps > 0 ? 'text-red-600 dark:text-red-400' : undefined} />
                  <StatBox label="Leads Assigned" value={detail.leadsAssigned} />
                  {detail.dealValueWon > 0 && <StatBox label="Deal Value Won" value={`₹${(detail.dealValueWon / 1000).toFixed(0)}K`} highlight="text-emerald-600 dark:text-emerald-400" />}
                </>
              )}
            </div>

            {/* Call Breakdown by Status */}
            {detail?.callsByStatus && Object.keys(detail.callsByStatus).length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Call Breakdown</h4>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(detail.callsByStatus).map(([status, count]) => (
                    <span key={status} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                      {status.replace(/_/g, ' ')}: <span className="font-bold">{count}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Interactions (Activity Trail) */}
            {detail?.recentInteractions && detail.recentInteractions.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Activity Trail</h4>
                <div className="space-y-2">
                  {detail.recentInteractions.map((interaction) => {
                    const typeConfig: Record<string, { icon: string; color: string }> = {
                      call: { icon: '📞', color: 'bg-indigo-50 dark:bg-indigo-900/20' },
                      visit: { icon: '📍', color: 'bg-amber-50 dark:bg-amber-900/20' },
                      whatsapp: { icon: '💬', color: 'bg-green-50 dark:bg-green-900/20' },
                      email: { icon: '📧', color: 'bg-blue-50 dark:bg-blue-900/20' },
                      note: { icon: '📝', color: 'bg-gray-50 dark:bg-gray-700/50' },
                    };
                    const cfg = typeConfig[interaction.type] || typeConfig.note;
                    return (
                      <div key={interaction.id} className={`px-3 py-2.5 rounded-lg ${cfg.color} text-sm`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-gray-900 dark:text-white">
                            {cfg.icon} {interaction.contactName}
                          </span>
                          <div className="flex items-center gap-2">
                            {interaction.outcome && (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-white/60 dark:bg-gray-600 text-gray-600 dark:text-gray-300 capitalize">
                                {interaction.outcome.replace(/_/g, ' ')}
                              </span>
                            )}
                            <span className="text-xs text-gray-400">{new Date(interaction.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>
                          </div>
                        </div>
                        {interaction.summary && (
                          <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">{interaction.summary}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Recent Calls with Notes */}
            {detail?.recentCalls && detail.recentCalls.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Call History</h4>
                <div className="space-y-2">
                  {detail.recentCalls.slice(0, 10).map((call) => (
                    <div key={call.id} className="px-3 py-2.5 rounded-lg bg-gray-50 dark:bg-gray-700/50 text-sm">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Phone className="w-3.5 h-3.5 text-gray-400" />
                          <span className="text-gray-900 dark:text-white font-medium">{call.contactName || call.phoneNumber}</span>
                          {call.contactName && <span className="text-gray-400 text-xs font-mono">{call.phoneNumber}</span>}
                        </div>
                        <div className="flex items-center gap-3">
                          <CallStatusBadge status={call.status} />
                          <span className="text-gray-500 dark:text-gray-400 text-xs">{formatDuration(call.duration)}</span>
                          <span className="text-xs text-gray-400">{new Date(call.startTime).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>
                        </div>
                      </div>
                      {(call.callSummary || call.notes) && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5 leading-relaxed pl-5">
                          {call.callSummary || call.notes}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function StatBox({ label, value, highlight }: { label: string; value: string | number; highlight?: string }) {
  return (
    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 text-center">
      <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
      <p className={`text-lg font-bold mt-0.5 ${highlight || 'text-gray-900 dark:text-white'}`}>{value}</p>
    </div>
  );
}

function CallStatusBadge({ status }: { status: string }) {
  const config: Record<string, string> = {
    CONNECTED: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    NO_ANSWER: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    BUSY: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    FAILED: 'bg-gray-100 text-gray-600 dark:bg-gray-600 dark:text-gray-300',
    VOICEMAIL: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${config[status] || config.FAILED}`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}

// ─── Main Page ───

export function TeamPerformancePage() {
  const [period, setPeriod] = useState<Period>('today');
  const [role, setRole] = useState<UserRole | null>(null);
  const [teamData, setTeamData] = useState<TeamData | null>(null);
  const [personalStats, setPersonalStats] = useState<AggregateStats | null>(null);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [teams, setTeams] = useState<TeamInfo[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Member detail modal
  const [selectedMember, setSelectedMember] = useState<MemberStats | null>(null);
  const [memberDetail, setMemberDetail] = useState<MemberDetail | null>(null);
  const [memberDetailLoading, setMemberDetailLoading] = useState(false);

  // Determine user role on mount
  useEffect(() => {
    detectRole();
  }, []);

  async function detectRole() {
    try {
      const { data } = await api.get('/sales-teams/my-team');
      if (data.success && data.data) {
        const myRole: string = data.data.myRole || '';
        if (['ADMIN', 'SUPER_ADMIN'].includes(myRole.toUpperCase())) {
          setRole('admin');
        } else if (['MANAGER', 'TEAM_LEAD', 'BACKEND_CRM'].includes(myRole.toUpperCase())) {
          setRole('manager');
        } else {
          setRole('executive');
        }
      } else {
        // No team — check if admin by trying to list teams
        try {
          const teamsRes = await api.get('/sales-teams/');
          if (teamsRes.data.success && Array.isArray(teamsRes.data.data) && teamsRes.data.data.length > 0) {
            setRole('admin');
          } else {
            setRole('executive');
          }
        } catch {
          setRole('executive');
        }
      }
    } catch {
      setRole('executive');
    }
  }

  // Load data when period or role changes
  useEffect(() => {
    if (role) loadData();
  }, [period, role, selectedTeamId]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      if (role === 'admin') {
        // Load all teams list if not loaded
        if (teams.length === 0) {
          try {
            const teamsRes = await api.get('/sales-teams/');
            if (teamsRes.data.success && Array.isArray(teamsRes.data.data)) {
              setTeams(teamsRes.data.data);
              if (!selectedTeamId && teamsRes.data.data.length > 0) {
                setSelectedTeamId(teamsRes.data.data[0].id);
                setLoading(false);
                return; // Will re-trigger via useEffect
              }
            }
          } catch {
            // Fall through
          }
        }

        // Load team performance + leaderboard
        const [teamRes, lbRes] = await Promise.all([
          api.get('/telecaller/reports/team-performance', { params: { period } }),
          api.get('/telecaller/reports/leaderboard', { params: { period } }),
        ]);
        if (teamRes.data.success) setTeamData(teamRes.data.data);
        if (lbRes.data.success) setLeaderboard(lbRes.data.data || []);

      } else if (role === 'manager') {
        // Manager sees team + own stats + leaderboard
        const [teamRes, selfRes, lbRes] = await Promise.all([
          api.get('/telecaller/reports/team-performance', { params: { period } }),
          api.get('/telecaller/reports/agent-performance', { params: { period } }),
          api.get('/telecaller/reports/leaderboard', { params: { period } }),
        ]);
        if (teamRes.data.success) setTeamData(teamRes.data.data);
        if (selfRes.data.success) setPersonalStats(selfRes.data.data);
        if (lbRes.data.success) setLeaderboard(lbRes.data.data || []);

      } else {
        // Executive sees only own stats + leaderboard
        const [selfRes, lbRes] = await Promise.all([
          api.get('/telecaller/reports/agent-performance', { params: { period } }),
          api.get('/telecaller/reports/leaderboard', { params: { period } }),
        ]);
        if (selfRes.data.success) setPersonalStats(selfRes.data.data);
        if (lbRes.data.success) setLeaderboard(lbRes.data.data || []);
      }
    } catch (err: any) {
      console.error('Failed to load team performance:', err);
      setError(err?.response?.data?.message || 'Failed to load performance data');
    } finally {
      setLoading(false);
    }
  }, [role, period, selectedTeamId, teams.length]);

  // Load member detail
  async function loadMemberDetail(member: MemberStats) {
    setSelectedMember(member);
    setMemberDetail(null);
    setMemberDetailLoading(true);
    try {
      const { data } = await api.get('/telecaller/reports/team-member-stats', {
        params: { memberId: member.agent.id, period },
      });
      if (data.success) setMemberDetail(data.data);
    } catch (err) {
      console.error('Failed to load member detail:', err);
    } finally {
      setMemberDetailLoading(false);
    }
  }

  // ─── Render ───

  if (!role) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Team Performance</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {role === 'executive' ? 'Your calling performance' : 'Monitor team calling metrics and agent performance'}
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* Admin team selector */}
          {role === 'admin' && teams.length > 1 && (
            <div className="relative">
              <select
                value={selectedTeamId}
                onChange={(e) => setSelectedTeamId(e.target.value)}
                className="appearance-none bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 pr-8 text-sm font-medium text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {teams.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          )}

          {/* Period tabs */}
          <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-0.5">
            {(['today', 'week', 'month'] as Period[]).map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  period === p
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`}
              >
                {PERIOD_LABELS[p]}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
            <p className="text-sm text-gray-500 dark:text-gray-400">Loading performance data...</p>
          </div>
        </div>
      ) : (
        <>
          {/* Executive View — Personal Stats */}
          {role === 'executive' && personalStats && (
            <PersonalStatsSection stats={personalStats} />
          )}

          {/* Manager/Admin View — Team Aggregate */}
          {(role === 'manager' || role === 'admin') && teamData && (
            <>
              {/* Team Name Banner */}
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-indigo-500" />
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{teamData.teamName}</h2>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  &middot; {teamData.memberCount} member{teamData.memberCount !== 1 ? 's' : ''}
                </span>
              </div>

              {/* Aggregate KPI Cards */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <KpiCard
                  icon={Phone}
                  label="Total Calls"
                  value={teamData.aggregate.totalCalls.toLocaleString('en-IN')}
                  subValue={`${teamData.aggregate.connectedCalls} connected`}
                  color="text-indigo-600 dark:text-indigo-400"
                  iconBg="bg-indigo-50 dark:bg-indigo-900/30"
                />
                <KpiCard
                  icon={PhoneIncoming}
                  label="Connect Rate"
                  value={formatRate(teamData.aggregate.connectRate)}
                  color={getRateColor(teamData.aggregate.connectRate)}
                  iconBg={getRateBg(teamData.aggregate.connectRate)}
                />
                <KpiCard
                  icon={Target}
                  label="Deals Won"
                  value={teamData.dealsWon}
                  color="text-emerald-600 dark:text-emerald-400"
                  iconBg="bg-emerald-50 dark:bg-emerald-900/30"
                />
                <KpiCard
                  icon={Clock}
                  label="Avg Duration"
                  value={formatDurationShort(teamData.aggregate.avgDuration)}
                  subValue={`${formatDuration(teamData.aggregate.totalDuration)} total`}
                  color="text-blue-600 dark:text-blue-400"
                  iconBg="bg-blue-50 dark:bg-blue-900/30"
                />
                <KpiCard
                  icon={TrendingUp}
                  label="Conversions"
                  value={teamData.aggregate.conversions}
                  color="text-violet-600 dark:text-violet-400"
                  iconBg="bg-violet-50 dark:bg-violet-900/30"
                />
                <KpiCard
                  icon={CalendarCheck}
                  label="Pending Follow-ups"
                  value={teamData.pendingFollowUps}
                  subValue={`${teamData.aggregate.followUpsDone} completed`}
                  color="text-amber-600 dark:text-amber-400"
                  iconBg="bg-amber-50 dark:bg-amber-900/30"
                />
              </div>

              {/* Member Breakdown Table */}
              {teamData.members.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
                  <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                      <UserCheck className="w-4 h-4 text-gray-400" />
                      Member Breakdown
                    </h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 dark:bg-gray-700/50 text-left">
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Agent</th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Role</th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide text-right">Calls</th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide text-right">Connected</th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide text-right">Rate</th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide text-right">Conversions</th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide text-right">Duration</th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide text-right">Follow-ups</th>
                          <th className="px-4 py-3 w-8"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                        {teamData.members.map((m) => (
                          <tr
                            key={m.agent.id}
                            onClick={() => loadMemberDetail(m)}
                            className="hover:bg-gray-50 dark:hover:bg-gray-700/30 cursor-pointer transition-colors"
                          >
                            <td className="px-4 py-3 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                              {m.agent.firstName} {m.agent.lastName}
                            </td>
                            <td className="px-4 py-3 text-gray-500 dark:text-gray-400 capitalize whitespace-nowrap">
                              {m.role.toLowerCase().replace(/_/g, ' ')}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300 font-semibold tabular-nums">
                              {m.totalCalls}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300 tabular-nums">
                              {m.connectedCalls}
                            </td>
                            <td className="px-4 py-3 text-right tabular-nums">
                              <span className={`font-semibold ${getRateColor(m.connectRate)}`}>
                                {formatRate(m.connectRate)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300 tabular-nums">
                              {m.conversions}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-500 dark:text-gray-400 tabular-nums whitespace-nowrap">
                              {formatDuration(m.totalDuration)}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-500 dark:text-gray-400 tabular-nums">
                              {m.followUpsSet}/{m.followUpsDone}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <ChevronRight className="w-4 h-4 text-gray-300 dark:text-gray-600" />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Manager Personal Stats (below team) */}
          {role === 'manager' && personalStats && (
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3">
                <Shield className="w-4 h-4 text-gray-400" />
                Your Performance
              </h3>
              <PersonalStatsSection stats={personalStats} />
            </div>
          )}

          {/* Leaderboard */}
          {leaderboard.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <Award className="w-4 h-4 text-amber-500" />
                  Leaderboard — {PERIOD_LABELS[period]}
                </h3>
              </div>
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {leaderboard.map((entry, idx) => {
                  const rank = entry.rank || idx + 1;
                  const medalColor =
                    rank === 1 ? 'text-amber-500' :
                    rank === 2 ? 'text-gray-400' :
                    rank === 3 ? 'text-amber-700' :
                    'text-gray-300 dark:text-gray-600';

                  return (
                    <div key={entry.agent.id} className="flex items-center gap-4 px-5 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                      <div className="w-8 text-center">
                        {rank <= 3 ? (
                          <Award className={`w-5 h-5 mx-auto ${medalColor}`} />
                        ) : (
                          <span className="text-sm font-bold text-gray-400 dark:text-gray-500">{rank}</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {entry.agent.firstName} {entry.agent.lastName}
                        </p>
                      </div>
                      <div className="flex items-center gap-5 text-xs text-gray-500 dark:text-gray-400">
                        <div className="text-center">
                          <p className="font-bold text-gray-700 dark:text-gray-300 text-sm tabular-nums">{entry.totalCalls}</p>
                          <p>calls</p>
                        </div>
                        <div className="text-center">
                          <p className={`font-bold text-sm tabular-nums ${getRateColor(entry.connectRate)}`}>{formatRate(entry.connectRate)}</p>
                          <p>rate</p>
                        </div>
                        <div className="text-center">
                          <p className="font-bold text-emerald-600 dark:text-emerald-400 text-sm tabular-nums">{entry.conversions}</p>
                          <p>conv.</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && !teamData && !personalStats && leaderboard.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <BarChart3 className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">No performance data yet</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-sm">
                Start making calls to see your performance metrics here. Data updates in real time.
              </p>
            </div>
          )}
        </>
      )}

      {/* Member Detail Modal */}
      {selectedMember && (
        <MemberDetailModal
          member={selectedMember}
          detail={memberDetail}
          loading={memberDetailLoading}
          period={period}
          onClose={() => { setSelectedMember(null); setMemberDetail(null); }}
        />
      )}
    </div>
  );
}

// ─── Personal Stats Section ───

function PersonalStatsSection({ stats }: { stats: AggregateStats }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
      <KpiCard
        icon={Phone}
        label="Total Calls"
        value={stats.totalCalls.toLocaleString('en-IN')}
        color="text-indigo-600 dark:text-indigo-400"
        iconBg="bg-indigo-50 dark:bg-indigo-900/30"
      />
      <KpiCard
        icon={PhoneCall}
        label="Connected"
        value={stats.connectedCalls.toLocaleString('en-IN')}
        subValue={formatRate(stats.connectRate) + ' rate'}
        color="text-green-600 dark:text-green-400"
        iconBg="bg-green-50 dark:bg-green-900/30"
      />
      <KpiCard
        icon={Target}
        label="Conversions"
        value={stats.conversions}
        color="text-emerald-600 dark:text-emerald-400"
        iconBg="bg-emerald-50 dark:bg-emerald-900/30"
      />
      <KpiCard
        icon={Timer}
        label="Talk Time"
        value={formatDuration(stats.totalDuration)}
        subValue={`Avg ${formatDuration(stats.avgDuration)}`}
        color="text-blue-600 dark:text-blue-400"
        iconBg="bg-blue-50 dark:bg-blue-900/30"
      />
      <KpiCard
        icon={CalendarCheck}
        label="Follow-ups Set"
        value={stats.followUpsSet}
        color="text-violet-600 dark:text-violet-400"
        iconBg="bg-violet-50 dark:bg-violet-900/30"
      />
      <KpiCard
        icon={TrendingUp}
        label="Follow-ups Done"
        value={stats.followUpsDone}
        color="text-amber-600 dark:text-amber-400"
        iconBg="bg-amber-50 dark:bg-amber-900/30"
      />
    </div>
  );
}
